import { defineStore } from 'pinia';
import state from './state';

import {
  APP_ACCESS_TOKEN_KEY,
  AWS_ACCESS_TOKEN_KEY,
  AWS_COGNITO_MFA_ACCESS_TOKEN_KEY,
  AWS_FEDERATED_ACCESS_TOKEN_KEY,
  AWS_REFRESH_TOKEN_KEY,
  AWS_REGISTER_SESSION_TOKEN_KEY,
  PKCE_CODE_VERIFIER_KEY,
  USER_EMAIL_KEY,
} from '@/utils/constants';

import type { SetTokenDTO } from '@/types/Auth';

import { useUserStore } from '@/stores/user';

export const useAuthStore = defineStore('auth', {
  state,
  actions: {
    setAccessToken(token: string) {
      this.accessToken = token;
      localStorage.setItem(APP_ACCESS_TOKEN_KEY, token);
    },

    setRegisterInfo(values: SetTokenDTO) {
      this.registerToken = values.token;
      this.email = values.email;
      this.password = values.password ?? '';
      localStorage.setItem(AWS_REGISTER_SESSION_TOKEN_KEY, values.token);
      localStorage.setItem(USER_EMAIL_KEY, values.email);
    },

    clearRegisterInfo() {
      this.registerToken = null;
      this.password = null;
      this.isOtpSetup = false;
      this.isProfileSetup = false;
      this.isMfaSetup = true;
      localStorage.removeItem(AWS_REGISTER_SESSION_TOKEN_KEY);
    },

    setEmail(email: string) {
      this.email = email;
      localStorage.setItem(USER_EMAIL_KEY, email);
    },

    clearEmail() {
      this.email = null;
      localStorage.removeItem(USER_EMAIL_KEY);
    },

    setAWSAccessToken(awsToken: string) {
      this.awsAccessToken = awsToken;
      localStorage.setItem(AWS_ACCESS_TOKEN_KEY, awsToken);
    },

    setRefreshToken(refreshToken: string) {
      this.refreshToken = refreshToken;
      localStorage.setItem(AWS_REFRESH_TOKEN_KEY, refreshToken);
    },

    setCognitoMfaAccessToken(mfaToken: string) {
      this.cognitoMfaAccessToken = mfaToken;
      localStorage.setItem(AWS_COGNITO_MFA_ACCESS_TOKEN_KEY, mfaToken);
    },

    setFederatedAccessToken(federatedToken: string) {
      this.federatedAccessToken = federatedToken;
      localStorage.setItem(AWS_FEDERATED_ACCESS_TOKEN_KEY, federatedToken);
    },

    clearMFAInfo() {
      this.isOtpSetup = false;
      this.isMfaSetup = false;
      this.isProfileSetup = true;
    },

    clearCognitoMfaAccessToken() {
      this.cognitoMfaAccessToken = null;
      localStorage.removeItem(AWS_COGNITO_MFA_ACCESS_TOKEN_KEY);
    },

    clearFederatedAccessToken() {
      this.federatedAccessToken = null;
      this.authType = 'email';
      localStorage.removeItem(AWS_FEDERATED_ACCESS_TOKEN_KEY);
    },

    logout() {
      this.accessToken = null;
      this.registerToken = null;
      this.awsAccessToken = null;
      this.refreshToken = null;
      this.federatedAccessToken = null;
      this.email = null;
      localStorage.clear();
      sessionStorage.clear();

      const userStore = useUserStore();
      userStore.clearUser();
    },

    clearSettingSetup() {
      this.isProfileSetup = false;
      this.isOtpSetup = false;
      this.isMfaSetup = false;
      this.registerToken = null;
      this.sendingVerify = false;
      this.isInvitedRegistration = false;
    },

    clearCodeVerifier() {
      sessionStorage.removeItem(PKCE_CODE_VERIFIER_KEY);
    },

    clearAll() {
      localStorage.clear();
      sessionStorage.clear();

      const userStore = useUserStore();
      userStore.clearUser();
    },
  },

  getters: {
    loggedIn: (state) => !!state.accessToken,
    awsLoggedIn: (state) => !!state.awsAccessToken,
    registering: (state) => !!state.registerToken,
    getEmail: (state) => state?.email ?? '',
  },

  persist: true,
});
